/**
 * AggregateWizard — model-editor sheet for registering a pre-aggregated
 * fact table. Two paths:
 *   • Managed: INZARA builds & maintains (server does the roll-up).
 *   • External: customer points at an existing table they refresh
 *     themselves. Drift-check button lives on the governance tab.
 *
 * Phase 2 measure_map kinds:
 *   SUM      — simple additive
 *   COUNT    — stored as count(*), rolls up via SUM
 *   MIN / MAX — extremes
 *   SUM_COUNT — pair of columns so AVG becomes weighted at query time
 *   HLL      — approximate DISTINCT COUNT via a stored value set
 */
import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/combobox';
import { Loader2, Layers, Table2, RefreshCcw, AlertTriangle, CheckCircle2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useBrandName } from '@/lib/branding';
import { pushOverlay, popOverlay } from '@/lib/overlayState';

const AGG_KINDS = [
  { value: 'SUM', label: 'SUM', hint: 'additive numeric — total revenue, losses, count sums' },
  { value: 'COUNT', label: 'COUNT', hint: 'row count in this bucket' },
  { value: 'MIN', label: 'MIN', hint: 'lowest value' },
  { value: 'MAX', label: 'MAX', hint: 'highest value' },
  { value: 'SUM_COUNT', label: 'SUM_COUNT (for AVG)', hint: 'stores sum + count so AVG stays exact at any grain' },
  { value: 'HLL', label: 'HLL (for DISTINCT COUNT)', hint: 'stores a value set so DISTINCT COUNT is approximate but ~200× faster' },
  { value: 'TDIGEST', label: 'TDIGEST (for percentiles)', hint: 'stores value samples so MEDIAN / P90 / P95 / P99 route to the aggregate' },
];

const OPS = [
  { value: 'equals', label: '=' },
  { value: 'not_equals', label: '≠' },
  { value: 'in', label: 'in' },
  { value: 'not_in', label: 'not in' },
  { value: 'between', label: 'between' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'is_null', label: 'is null' },
  { value: 'not_null', label: 'not null' },
];

// NULL checks take no operand — the value input is hidden and no value persists.
const isNullOp = (op) => op === 'is_null' || op === 'not_null';
// IN / NOT IN take a comma-separated list → persisted as an array (what the
// query engine's $in / SQL IN (…) paths expect).
const isListOp = (op) => op === 'in' || op === 'not_in';
// BETWEEN takes a low+high pair → persisted as a [low, high] array; numeric
// strings are coerced to numbers so range comparisons work on numeric columns.
const isRangeOp = (op) => op === 'between';
const coerceScalar = (v) => {
  const s = String(v == null ? '' : v).trim();
  if (s !== '' && !isNaN(Number(s))) return Number(s);
  return s;
};
const listTokens = (v) => String(v == null ? '' : v).split(',').map(s => s.trim()).filter(Boolean);

// Name-based predicate suggestion: infer a conditional-aggregate predicate
// from the measure's name against the canonical's dimensions. e.g. a
// measure `new_business_written_premium` with a dim `new_or_renewal` →
// suggest new_or_renewal='new'; `written_premium` with `amount_type_code`
// → amount_type_code='premium'. Heuristic only — modeler edits after.
function suggestPredicate(measureName, dims) {
  const mn = (measureName || '').toLowerCase();
  const preds = [];
  const CODE_HINT = /(type|code|category|status|flag|kind|_or_)/i;
  const TOKENS = {
    premium: 'premium', fee: 'fee', tax: 'tax', commission: 'commission',
    loss: 'loss', reserve: 'reserve', new: 'new', renewal: 'renewal', renew: 'renewal',
  };
  for (const d of dims) {
    if (!CODE_HINT.test(d.name)) continue;
    for (const [tok, val] of Object.entries(TOKENS)) {
      if (mn.includes(tok)) {
        if (!preds.some(p => p.field === d.name)) {
          preds.push({ field: d.name, operator: 'equals', value: val });
        }
        break;
      }
    }
  }
  return preds;
}

// Client mirror of the backend column-name similarity used to auto-suggest a
// canonical column for a conformed (renamed) summary dimension.
function bestCanonicalMatch(col, canonicalNames) {
  const cu = (col || '').toUpperCase();
  let best = null, score = 0;
  for (const cc of canonicalNames) {
    const ccu = (cc || '').toUpperCase();
    let s = 0;
    if (cu && ccu && cu.includes(ccu)) s = ccu.length;
    else if (cu && ccu && ccu.includes(cu)) s = cu.length;
    else {
      let n = 0;
      for (let i = 1; i <= Math.min(cu.length, ccu.length); i++) {
        if (cu[cu.length - i] === ccu[ccu.length - i]) n++; else break;
      }
      s = n;
    }
    if (s > score) { score = s; best = cc; }
  }
  return score >= 4 ? best : null;
}

// Client mirror of the backend `_default_time_grain` — infers the summary's
// temporal grain from its grain column names for the router's coarse-grain gate.
function defaultTimeGrain(grainCols) {
  for (const g of grainCols) {
    const gl = (g || '').toLowerCase();
    if (gl.includes('week')) return 'week';
    if (gl.endsWith('_day') || gl === 'day' || gl.includes('dayofmonth')) return 'day';
    if (gl.includes('quarter')) return 'quarter';
  }
  for (const g of grainCols) if ((g || '').toLowerCase().includes('year')) return 'year';
  for (const g of grainCols) {
    const gl = (g || '').toLowerCase();
    if (['month', 'period', 'yyyymm', '_end', 'date', '_dt'].some(t => gl.includes(t))) return 'month';
  }
  return '';
}

const TIME_GRAINS = [
  { value: '__none__', label: 'None (no grain gate)' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
];

const PERIOD_PARTS = [
  { value: '__none__', label: 'direct' },
  { value: 'year', label: 'year(…)' },
  { value: 'quarter', label: 'quarter(…)' },
  { value: 'month', label: 'month(…)' },
];

// ── Predicate tree helpers ───────────────────────────────────────────────
// Editing shape: leaf  = {kind:'leaf', field, operator, value}
//                group = {kind:'group', bool:'and'|'or', negated, children:[]}
// Backend shape: flat list [{field,operator,value}] (AND-ed, legacy) OR a tree
//                {bool,args:[...]} | {not:node} | leaf — matches the E-CALC
//                scope-condition tree the query engine already understands.
function leafToEdit(c) {
  const op = c.operator || 'equals';
  let value = c.value == null ? '' : c.value;
  if (op === 'between') {
    const a = Array.isArray(value) ? value : [];
    value = [a[0] == null ? '' : String(a[0]), a[1] == null ? '' : String(a[1])];
  } else if (Array.isArray(value)) {
    value = value.join(', ');
  }
  return { kind: 'leaf', field: c.field || '', operator: op, value };
}
function nodeToEdit(n) {
  if (!n) return { kind: 'group', bool: 'and', negated: false, children: [] };
  if (Array.isArray(n)) return { kind: 'group', bool: 'and', negated: false, children: n.map(leafToEdit) };
  if (n.not) {
    const inner = nodeToEdit(n.not);
    if (inner.kind === 'group') return { ...inner, negated: !inner.negated };
    return { kind: 'group', bool: 'and', negated: true, children: [inner] };
  }
  if (n.bool) return { kind: 'group', bool: n.bool, negated: false, children: (n.args || []).map(nodeToEdit) };
  return leafToEdit(n);
}
function editFields(node) {
  const out = new Set();
  const walk = (n) => { if (!n) return; if (n.kind === 'leaf') { if (n.field) out.add(n.field); } else (n.children || []).forEach(walk); };
  walk(node);
  return Array.from(out);
}
function pruneEdit(node) {
  if (!node) return null;
  if (node.kind === 'leaf') {
    if (!node.field) return null;
    if (isListOp(node.operator)) { if (listTokens(node.value).length === 0) return null; return node; }
    if (isRangeOp(node.operator)) {
      const a = Array.isArray(node.value) ? node.value : [];
      if (String(a[0] ?? '').trim() === '' || String(a[1] ?? '').trim() === '') return null;
      return node;
    }
    if (!isNullOp(node.operator) && (node.value === '' || node.value == null)) return null;
    return node;
  }
  const children = (node.children || []).map(pruneEdit).filter(Boolean);
  if (children.length === 0) return null;
  return { ...node, children };
}
function leafToBackend(l) {
  if (isNullOp(l.operator)) return { field: l.field, operator: l.operator };
  if (isListOp(l.operator)) return { field: l.field, operator: l.operator, value: listTokens(l.value) };
  if (isRangeOp(l.operator)) { const a = Array.isArray(l.value) ? l.value : ['', '']; return { field: l.field, operator: l.operator, value: [coerceScalar(a[0]), coerceScalar(a[1])] }; }
  return { field: l.field, operator: l.operator, value: l.value };
}
function editToBackendNode(node) {
  if (node.kind === 'leaf') return leafToBackend(node);
  const args = node.children.map(editToBackendNode);
  let out = (args.length === 1 && node.bool === 'and') ? args[0] : { bool: node.bool, args };
  if (node.negated) out = { not: out };
  return out;
}
// Serialize an editing root to the backend shape. Emits a FLAT LIST when it's a
// plain single-level AND of leaves (backward compatible); otherwise a tree.
function serializePredicate(root) {
  const pruned = pruneEdit(root);
  if (!pruned) return null;
  if (pruned.kind === 'group' && pruned.bool === 'and' && !pruned.negated && pruned.children.every(c => c.kind === 'leaf' && c.operator !== 'between')) {
    return pruned.children.map(leafToBackend);
  }
  return editToBackendNode(pruned);
}
// Canonical comparable string for a backend-shape predicate (flat OR tree) —
// used to flag cross-binding measure definition conflicts.
function canonPred(pred) {
  if (!pred) return '';
  const vstr = (c) => isNullOp(c.operator) ? '' : (Array.isArray(c.value) ? c.value.join(',') : c.value);
  const leaf = (c) => `${c.field}|${c.operator || 'equals'}|${vstr(c)}`;
  if (Array.isArray(pred)) return pred.filter(c => c && c.field).map(leaf).sort().join(' & ');
  const node = (n) => {
    if (n.bool) return `(${n.bool}:${(n.args || []).map(node).sort().join(',')})`;
    if (n.not) return `not(${node(n.not)})`;
    return leaf(n);
  };
  return node(pred);
}

// Human-readable summary of an editing tree, e.g.
// "region = west OR (status ≠ void AND amount > 10)". Returns '' when empty.
const OP_TEXT = { equals: '=', not_equals: '≠', in: 'in', not_in: 'not in', gt: '>', gte: '≥', lt: '<', lte: '≤', is_null: 'is null', not_null: 'is not null' };
function predToText(node, top = true) {
  if (!node) return '';
  if (node.kind === 'leaf') {
    if (!node.field) return '';
    const op = OP_TEXT[node.operator] || node.operator;
    if (isNullOp(node.operator)) return `${node.field} ${op}`;
    if (isListOp(node.operator)) {
      const toks = listTokens(node.value);
      if (toks.length === 0) return '';
      return `${node.field} ${op} [${toks.join(', ')}]`;
    }
    if (isRangeOp(node.operator)) {
      const a = Array.isArray(node.value) ? node.value : [];
      if (String(a[0] ?? '').trim() === '' || String(a[1] ?? '').trim() === '') return '';
      return `${node.field} between ${a[0]} and ${a[1]}`;
    }
    if (node.value === '' || node.value == null) return '';
    return `${node.field} ${op} ${node.value}`;
  }
  const parts = (node.children || []).map(c => predToText(c, false)).filter(Boolean);
  if (parts.length === 0) return '';
  const body = parts.join(node.bool === 'and' ? ' AND ' : ' OR ');
  if (node.negated) return `NOT (${body})`;
  if (!top && (parts.length > 1)) return `(${body})`;
  return body;
}

// Recursive leaf row: field / operator / value.
function PredicateLeaf({ node, onChange, onRemove, fields, tid, selected, onToggleSelect }) {
  return (
    <div className="flex items-center gap-1" data-testid={`${tid}-leaf`}>
      {onToggleSelect && (
        <input type="checkbox" checked={!!selected} onChange={onToggleSelect}
          className="h-3 w-3 shrink-0 accent-primary cursor-pointer" title="Select to group"
          data-testid={`${tid}-select`} />
      )}
      <Select value={node.field} onValueChange={(v) => onChange({ ...node, field: v })}>
        <SelectTrigger className="h-6 flex-1 text-[10px]" data-testid={`${tid}-field`}><SelectValue placeholder="field" /></SelectTrigger>
        <SelectContent>{fields.map(d => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={node.operator} onValueChange={(v) => {
        let value = node.value;
        if (isNullOp(v)) value = '';
        else if (v === 'between') value = Array.isArray(node.value) ? node.value : ['', ''];
        else if (node.operator === 'between') value = '';
        onChange({ ...node, operator: v, value });
      }}>
        <SelectTrigger className="h-6 w-20 text-[10px]" data-testid={`${tid}-op`}><SelectValue /></SelectTrigger>
        <SelectContent>{OPS.map(o => <SelectItem key={o.value} value={o.value} data-testid={`${tid}-op-option-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
      {isRangeOp(node.operator) ? (
        <div className="flex items-center gap-1">
          <Input value={(node.value || [])[0] || ''} onChange={(e) => onChange({ ...node, value: [e.target.value, (node.value || [])[1] || ''] })} placeholder="low" className="h-6 w-16 text-[10px] font-mono" data-testid={`${tid}-value-low`} />
          <span className="text-[9px] text-muted-foreground">and</span>
          <Input value={(node.value || [])[1] || ''} onChange={(e) => onChange({ ...node, value: [(node.value || [])[0] || '', e.target.value] })} placeholder="high" className="h-6 w-16 text-[10px] font-mono" data-testid={`${tid}-value-high`} />
        </div>
      ) : (!isNullOp(node.operator) && (
        <Input value={node.value} onChange={(e) => onChange({ ...node, value: e.target.value })} placeholder={isListOp(node.operator) ? 'a, b, c' : 'value'} className={`h-6 text-[10px] font-mono ${isListOp(node.operator) ? 'w-40' : 'w-32'}`} data-testid={`${tid}-value`} />
      ))}
      <button onClick={onRemove} className="text-[10px] text-muted-foreground hover:text-destructive px-1" data-testid={`${tid}-remove`}>×</button>
    </div>
  );
}

// Recursive AND/OR/NOT group box. depth caps nesting at a sane level.
function PredicateGroup({ node, onChange, onRemove, fields, depth = 0, tid }) {
  const children = node.children || [];
  const [sel, setSel] = useState(() => new Set());
  const setChild = (i, next) => onChange({ ...node, children: children.map((c, idx) => idx === i ? next : c) });
  const removeChild = (i) => { setSel(new Set()); onChange({ ...node, children: children.filter((_, idx) => idx !== i) }); };
  const addLeaf = () => onChange({ ...node, children: [...children, { kind: 'leaf', field: fields[0]?.name || '', operator: 'equals', value: '' }] });
  const addGroup = () => onChange({ ...node, children: [...children, { kind: 'group', bool: 'and', negated: false, children: [{ kind: 'leaf', field: fields[0]?.name || '', operator: 'equals', value: '' }] }] });
  const toggleSel = (i) => setSel(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  // Wrap the checked sibling conditions into a new nested AND group (in place),
  // so mixed precedence like (A AND B) OR (C AND D) is one click away: group
  // each pair, then flip this group to OR.
  const groupSelected = () => {
    const picked = [...sel].filter(i => children[i] && children[i].kind !== 'group').sort((a, b) => a - b);
    if (picked.length < 2) return;
    const pickedSet = new Set(picked);
    const grouped = picked.map(i => children[i]);
    const insertAt = picked[0];
    const before = children.slice(0, insertAt).filter((_, i) => !pickedSet.has(i));
    const after = children.slice(insertAt).filter((_, idx) => !pickedSet.has(insertAt + idx));
    const newGroup = { kind: 'group', bool: 'and', negated: false, children: grouped };
    onChange({ ...node, children: [...before, newGroup, ...after] });
    setSel(new Set());
  };
  const selectableSel = [...sel].filter(i => children[i] && children[i].kind !== 'group');
  const verb = node.negated ? 'none of' : (node.bool === 'and' ? 'all of' : 'any of');
  return (
    <div className={`rounded-lg border p-1.5 space-y-1 ${node.negated ? 'border-amber-500/40 bg-amber-500/5' : 'border-border/30'}`} data-testid={`${tid}-group`}>
      <div className="flex items-center gap-1.5">
        <div className="flex rounded-md overflow-hidden border border-border/40">
          <button onClick={() => onChange({ ...node, bool: 'and' })} className={`px-1.5 py-0.5 text-[9px] font-medium transition-colors ${node.bool === 'and' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`${tid}-bool-and`}>AND</button>
          <button onClick={() => onChange({ ...node, bool: 'or' })} className={`px-1.5 py-0.5 text-[9px] font-medium transition-colors ${node.bool === 'or' ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`${tid}-bool-or`}>OR</button>
        </div>
        <button onClick={() => onChange({ ...node, negated: !node.negated })} className={`px-1.5 py-0.5 rounded-md text-[9px] font-medium border transition-colors ${node.negated ? 'border-amber-500/50 bg-amber-500/10 text-amber-300' : 'border-border/40 text-muted-foreground hover:text-foreground'}`} data-testid={`${tid}-not`} title="Negate this whole group">NOT</button>
        <span className="text-[9px] text-muted-foreground">match {verb}</span>
        <div className="flex-1" />
        {onRemove && <button onClick={onRemove} className="text-[10px] text-muted-foreground hover:text-destructive px-1" data-testid={`${tid}-remove-group`}>× group</button>}
      </div>
      <div className="space-y-1 pl-1">
        {children.length === 0 && <p className="text-[9px] text-muted-foreground/60 italic">no conditions</p>}
        {children.map((c, i) => c.kind === 'group'
          ? React.createElement(PredicateGroup, { key: i, node: c, onChange: (n) => setChild(i, n), onRemove: () => removeChild(i), fields, depth: depth + 1, tid: `${tid}-${i}` })
          : <PredicateLeaf key={i} node={c} onChange={(n) => setChild(i, n)} onRemove={() => removeChild(i)} fields={fields} tid={`${tid}-${i}`}
              selected={sel.has(i)} onToggleSelect={depth < 3 ? () => toggleSel(i) : undefined} />
        )}
      </div>
      <div className="flex items-center gap-2 pl-1">
        <button onClick={addLeaf} className="text-[9px] text-primary/80 hover:text-primary" data-testid={`${tid}-add-condition`}>+ condition</button>
        {depth < 3 && <button onClick={addGroup} className="text-[9px] text-primary/80 hover:text-primary" data-testid={`${tid}-add-group`}>+ group</button>}
        {depth < 3 && selectableSel.length >= 2 && (
          <button onClick={groupSelected} className="text-[9px] font-medium text-primary bg-primary/10 border border-primary/30 rounded px-1.5 py-0.5 hover:bg-primary/20" data-testid={`${tid}-group-selected`}>⊞ Group {selectableSel.length} selected</button>
        )}
      </div>
    </div>
  );
}

export function AggregateWizard({ open, onOpenChange, modelId, onCreated, presetProposal, editAggregate }) {
  const brandName = useBrandName();
  useEffect(() => { if (!open) return; pushOverlay(); return () => popOverlay(); }, [open]);
  const [mode, setMode] = useState('managed');            // 'managed' | 'external'
  const [candidates, setCandidates] = useState([]);
  const [canonicalId, setCanonicalId] = useState('');
  const [name, setName] = useState('');
  const [tableName, setTableName] = useState('');
  const [grain, setGrain] = useState([]);                 // string[]
  const [measures, setMeasures] = useState([]);           // [{name, source_col, agg_stored}]
  const [refreshKind, setRefreshKind] = useState('nightly');
  const [watermarkCol, setWatermarkCol] = useState('last_updated_at');
  const [summaryCandidates, setSummaryCandidates] = useState([]);  // [{entity_id, name, table_name, columns, already_bound}]
  const [summaryEntityId, setSummaryEntityId] = useState('');
  const [tableColumns, setTableColumns] = useState([]);  // columns of the selected summary entity
  const [manualWatermark, setManualWatermark] = useState(false);
  const [dimensionMap, setDimensionMap] = useState({});   // {summaryDim: canonicalCol}
  const [extraMapKeys, setExtraMapKeys] = useState([]);   // summary cols added to map but not in grain (e.g. BOOK_END_DATE)
  const [newMapCol, setNewMapCol] = useState('');
  const [addMapError, setAddMapError] = useState('');
  const [timeGrain, setTimeGrain] = useState('');
  const [existingBindings, setExistingBindings] = useState([]);  // other aggregates in the model

  // Fetch modeled summary-entity candidates for the model when the user
  // switches to External mode. An external aggregate BINDS to an existing
  // modeled entity — it never creates a duplicate table.
  useEffect(() => {
    if (mode !== 'external' || !modelId) return;
    if (!editAggregate) setWatermarkCol('');
    let alive = true;
    api.getSummaryEntityCandidates(modelId)
      .then(res => { if (alive) setSummaryCandidates((res.candidates || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))); })
      .catch(() => { if (alive) setSummaryCandidates([]); });
    return () => { alive = false; };
  }, [mode, modelId]);

  // When a summary entity is picked, adopt its physical table + columns.
  const chooseSummaryEntity = (eid) => {
    setSummaryEntityId(eid);
    setManualWatermark(false);
    const hit = summaryCandidates.find(s => s.entity_id === eid);
    setTableName(hit?.table_name || '');
    setTableColumns(hit?.columns || []);
  };
  // Edit mode: the bound summary entity is hydrated from editAggregate, but its
  // columns aren't — populate tableColumns once candidates load so the watermark
  // stays a dropdown (not a free-form field) when editing.
  useEffect(() => {
    if (!summaryEntityId || !summaryCandidates.length) return;
    const hit = summaryCandidates.find(s => s.entity_id === summaryEntityId);
    if (hit && (hit.columns || []).length && !tableColumns.length) setTableColumns(hit.columns);
  }, [summaryEntityId, summaryCandidates]);
  // Edit mode self-heal: if the aggregate's stored summary_entity_id no longer
  // resolves to a candidate (stale/orphaned reference after a re-import or a
  // duplicate-entity merge — the common cause of the blank dropdown), remap to
  // the real bound summary entity that matches this aggregate by name. This
  // both populates the dropdown and repairs the binding on the next save.
  useEffect(() => {
    if (!editAggregate || !summaryEntityId || !summaryCandidates.length) return;
    if (summaryCandidates.some(s => s.entity_id === summaryEntityId)) return;
    const aggName = String(editAggregate.summary_entity_name || editAggregate.name || '').trim().toLowerCase();
    if (!aggName) return;
    const match = summaryCandidates.find(s => s.already_bound && String(s.name || '').trim().toLowerCase() === aggName)
      || summaryCandidates.find(s => String(s.name || '').trim().toLowerCase() === aggName);
    if (match) chooseSummaryEntity(match.entity_id);
  }, [summaryCandidates, summaryEntityId, editAggregate]);
  const [loading, setLoading] = useState(false);
  const [candidatesLoaded, setCandidatesLoaded] = useState(false);
  // While EDITing, the body can't render until the canonical candidates load
  // (hydration keys off `candidates`). On large models that round-trip takes
  // several seconds, so show a spinner instead of a blank "new aggregate" form.
  const editHydrating = !!editAggregate && open && !candidatesLoaded;
  const [buildStatus, setBuildStatus] = useState(null);   // {rows, entity_id} on success

  useEffect(() => {
    if (!open || !modelId) { setCandidatesLoaded(false); return; }
    (async () => {
      try {
        const res = await api.getAggregateCanonicalCandidates(modelId);
        setCandidates((res.candidates || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (e) { toast.error('Could not load canonical candidates'); }
      finally { setCandidatesLoaded(true); }
    })();
  }, [open, modelId]);

  // Load the model's other aggregate bindings so we can warn when a stored
  // column name we're about to define collides with an existing binding's
  // measure that has a DIFFERENT canonical source/predicate (which would make
  // the auto-generated canonical scoped measure ambiguous).
  useEffect(() => {
    if (!open || !modelId) { setExistingBindings([]); return; }
    let alive = true;
    api.listAggregatesForModel(modelId)
      .then(res => { if (alive) setExistingBindings(res.aggregates || []); })
      .catch(() => { if (alive) setExistingBindings([]); });
    return () => { alive = false; };
  }, [open, modelId]);

  // Hydrate from a saved suggestion when the caller passes `presetProposal`.
  // Runs once per open+preset — user can still edit after.
  useEffect(() => {
    if (!open || !presetProposal || candidates.length === 0) return;
    setMode('managed');
    setCanonicalId(presetProposal.canonical_entity_id || '');
    setName(presetProposal.proposed_name || '');
    setGrain(presetProposal.grain_columns || []);
    setMeasures(Object.entries(presetProposal.measure_map || {}).map(([mname, spec]) => ({
      uid: `m_${Math.random().toString(36).slice(2, 9)}`,
      name: mname,
      source_col: spec.source_col || mname,
      agg_stored: spec.agg_stored || 'SUM',
    })));
  }, [open, presetProposal, candidates]);

  // Hydrate for EDIT mode when the caller passes `editAggregate`.
  useEffect(() => {
    if (!open || !editAggregate || candidates.length === 0) return;
    setMode(editAggregate.refresh?.kind === 'external' ? 'external' : 'managed');
    setCanonicalId(editAggregate.derived_from_entity || '');
    setName(editAggregate.name || '');
    setTableName(editAggregate.table_name || '');
    setSummaryEntityId(editAggregate.summary_entity_id || '');
    setGrain(editAggregate.grain_columns || []);
    setMeasures(Object.entries(editAggregate.measure_map || {}).map(([mname, spec]) => ({
      uid: `m_${Math.random().toString(36).slice(2, 9)}`,
      name: mname,
      source_col: spec.source_col || mname,
      agg_stored: spec.agg_stored || 'SUM',
      summary_col: spec.summary_col || '',
      additivity: spec.additivity || 'additive',
      time_dim: spec.time_dim || '',
      predicate: nodeToEdit(spec.baked_predicate || []),
    })));
    setManualDecomp(editAggregate.decomposition_dims || []);
    setDimensionMap(editAggregate.dimension_map || {});
    setExtraMapKeys(Object.keys(editAggregate.dimension_map || {}));
    setTimeGrain(editAggregate.time_grain || '');
    if (editAggregate.refresh?.lag_watermark_column) setWatermarkCol(editAggregate.refresh.lag_watermark_column);
  }, [open, editAggregate, candidates]);

  // Edit mode: load the summary table's REAL physical columns so the mapping
  // left-side suggestions reflect actual summary columns (e.g. BOOK_END_DATE),
  // not canonical-named grain columns or canonical-only columns.
  const [summaryPhysicalCols, setSummaryPhysicalCols] = useState([]);
  useEffect(() => {
    if (!open || !editAggregate?.entity_id) { setSummaryPhysicalCols([]); return; }
    let alive = true;
    api.getAggregateSummaryColumns(editAggregate.entity_id)
      .then(res => { if (alive) setSummaryPhysicalCols(res?.columns || []); })
      .catch(() => { if (alive) setSummaryPhysicalCols([]); });
    return () => { alive = false; };
  }, [open, editAggregate]);

  // Re-introspect the summary table's physical columns from its data source
  // and persist them, so the mapping left-side reflects real summary columns.
  const [refreshingCols, setRefreshingCols] = useState(false);
  const refreshSummaryColumns = async () => {
    if (!editAggregate?.entity_id) return;
    setRefreshingCols(true);
    try {
      const res = await api.refreshAggregateSummaryColumns(editAggregate.entity_id);
      setSummaryPhysicalCols(res?.columns || []);
      if (res?.persisted) toast.success(res.message || `Refreshed ${(res.columns || []).length} columns from source.`);
      else toast.message(res?.message || 'Could not live-introspect this source.');
    } catch (e) {
      toast.error('Could not refresh columns from source');
    } finally {
      setRefreshingCols(false);
    }
  };


  const canonical = candidates.find(c => c.entity_id === canonicalId);
  const dims = canonical ? canonical.attributes.filter(a => a.semantic_type === 'dimension') : [];
  // Measure SOURCE candidates = any NUMERIC column (incl. hidden ones) plus
  // anything already classified as a measure. Imported warehouse facts often
  // classify amount columns as hidden identifiers, so filtering to
  // semantic_type === 'measure' alone would leave nothing to store.
  const _NUM_RE = /(int|float|double|decimal|numeric|real|number|money|long|bigint|smallint|tinyint)/i;
  const _isNumeric = (a) => a && a.data_type && _NUM_RE.test(String(a.data_type));
  // Surrogate keys / codes / flags / dates are numeric but never things you SUM.
  const _KEYISH_RE = /(_identifier$|_id$|_key$|_code$|_subcode$|_indicator$|_flag$|_date$|_name$|_deleted$|_number$|_num$)/i;
  const measureFields = canonical
    ? (() => {
        const nums = canonical.attributes.filter(a =>
          a.semantic_type === 'measure' ||
          (_isNumeric(a) && a.semantic_type !== 'dimension' && !_KEYISH_RE.test(a.name || '')));
        return nums.length ? nums : canonical.attributes; // never leave the picker empty
      })()
    : [];
  // Grain / predicate / decomposition candidates = every NON-measure field,
  // including hidden identifiers & keys. The fields that define a row's
  // combination (policy_id, month, type codes) are often hidden from end
  // users, but the modeler still needs them here.
  const grainFields = canonical ? canonical.attributes.filter(a => a.semantic_type !== 'measure') : [];
  const canonicalColNames = canonical ? canonical.attributes.map(a => a.name) : [];
  const canonicalColSet = new Set(canonicalColNames);
  // Edit mode: guarantee the already-bound summary entity is always selectable,
  // even if the candidates fetch hasn't resolved yet or the endpoint filtered it
  // out — otherwise the "Summary table (existing entity)" dropdown renders blank
  // when editing an external aggregate.
  const summaryEntityOptions = (() => {
    const list = summaryCandidates.slice();
    if (summaryEntityId && !list.some(s => s.entity_id === summaryEntityId)) {
      list.unshift({
        entity_id: summaryEntityId,
        name: editAggregate?.summary_entity_name || editAggregate?.name || summaryEntityId,
        table_name: editAggregate?.table_name || tableName || '',
        already_bound: true,
      });
    }
    return list;
  })();

  // Grain validation: a summary that declares a time grain needs SOME temporal
  // column to actually serve it — a date/timestamp-typed grain column, or a
  // dimension mapping that derives a period part / targets a canonical date
  // column. Without one, every date-based question silently falls back to the
  // transaction fact (the summary can never be used for a date query).
  const _DATE_RE = /(date|time|timestamp)/i;
  const _NAME_DATE_RE = /(_date$|_dt$|_month$|_year$|_quarter$|_qtr$|_week$|_period$|_yyyymm)/i;
  const _isDateCol = (name) => {
    if (!name || name === '__none__') return false;
    const a = canonical && canonical.attributes.find(x => x.name === name);
    if (a && a.data_type && _DATE_RE.test(String(a.data_type))) return true;
    return _NAME_DATE_RE.test(name);
  };
  const _grainHasTemporal = grain.some(_isDateCol);
  const _mapHasTemporal = Object.values(dimensionMap || {}).some(v => {
    if (v && typeof v === 'object') return (v.part && v.part !== '__none__') || _isDateCol(v.col);
    return _isDateCol(v);
  });
  const timeGrainMissingDateCol = !!timeGrain && grain.length > 0 && !_grainHasTemporal && !_mapHasTemporal;

  // Rows shown in the "summary → canonical" mapping: the grain columns PLUS any
  // summary column the user added to map (e.g. a date/period column like
  // BOOK_END_DATE that isn't a grain dimension). Dedup, grain first.
  const mapRowKeys = Array.from(new Set([...grain, ...extraMapKeys, ...Object.keys(dimensionMap || {})]));
  const mapRowKeySet = new Set(mapRowKeys);
  // Real summary columns to suggest/allow on the mapping LEFT side. Prefer the
  // live-introspected physical columns; fall back to the bound entity's columns.
  const summaryColOptions = (summaryPhysicalCols.length ? summaryPhysicalCols : tableColumns) || [];
  const summaryColSet = new Set(summaryColOptions.map(c => String(c).toUpperCase()));
  const addMapKey = (colArg) => {
    const col = String(colArg != null ? colArg : newMapCol).trim();
    if (!col) return;
    // Guard: a known CANONICAL-only column (e.g. TRANSACTION_BOOK_DATE, which
    // lives on the canonical fact — not the summary) must not be added as a
    // summary (left-side) key. A summary-only column the model doesn't know yet
    // (e.g. BOOK_END_DATE) is still allowed, since metadata can be incomplete.
    const U = col.toUpperCase();
    const canonicalUpper = new Set(canonicalColNames.map(c => String(c).toUpperCase()));
    if (canonicalUpper.has(U) && !summaryColSet.has(U)) {
      setAddMapError(`"${col}" is a column on the canonical fact, not the summary table${tableName ? ` ${tableName}` : ''}. The left side is for summary columns; choose the canonical column on the right.`);
      return;
    }
    setAddMapError('');
    setExtraMapKeys(prev => prev.includes(col) ? prev : [...prev, col]);
    setNewMapCol('');
  };

  // Auto-suggest dimension mappings + a default time grain whenever the
  // canonical or grain changes. Only fills gaps — never clobbers a user edit
  // or a hydrated (saved) mapping. Conformed-by-name grain columns are left
  // unmapped (they resolve directly on the canonical).
  useEffect(() => {
    if (!canonical) return;
    setDimensionMap(prev => {
      const next = { ...prev };
      // prune stale plain-string suggestions for removed grain cols, but keep
      // period (object) mappings for non-grain columns like BOOK_YEAR, and keep
      // user-added / saved mappings tracked in extraMapKeys (e.g. a manual
      // BOOK_END_DATE → TRANSACTION_BOOK_DATE date mapping) so they survive
      // save → close → reopen.
      for (const k of Object.keys(next)) if (!grain.includes(k) && !extraMapKeys.includes(k) && typeof next[k] !== 'object') delete next[k];
      for (const g of grain) {
        if (g in next || canonicalColSet.has(g)) continue;
        const m = bestCanonicalMatch(g, canonicalColNames);
        if (m) next[g] = m;
      }
      return next;
    });
    setTimeGrain(prev => prev || defaultTimeGrain(grain));
  }, [canonicalId, grain]);

  const toggleGrain = (col) => {
    setGrain(prev => prev.includes(col) ? prev.filter(g => g !== col) : [...prev, col]);
  };

  const addMeasure = (attr) => {
    const uid = `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
    setMeasures(prev => [...prev, { uid, name: attr.name, source_col: attr.name, agg_stored: 'SUM', additivity: 'additive', time_dim: '', predicate: nodeToEdit(suggestPredicate(attr.name, grainFields)) }]);
  };
  const removeMeasure = (uid) => setMeasures(prev => prev.filter(m => m.uid !== uid));
  const updateMeasure = (uid, patch) => setMeasures(prev => prev.map(m => m.uid === uid ? { ...m, ...patch } : m));
  const updateMeasureKind = (uid, kind) => updateMeasure(uid, { agg_stored: kind });

  // Decomposition dims = every dimension consumed by a measure predicate,
  // plus any the modeler flags manually. These are unsliceable from the
  // summary → the router falls back to the transaction fact when touched.
  const [manualDecomp, setManualDecomp] = useState([]);
  const autoDecomp = Array.from(new Set(measures.flatMap(m => editFields(m.predicate)).filter(Boolean)));
  const decompositionDims = Array.from(new Set([...autoDecomp, ...manualDecomp]));

  // Mapping-conflict guard: a stored column name we're defining also exists on
  // ANOTHER binding but with a different canonical source_col or baked
  // predicate → the auto-generated canonical scoped measure would be ambiguous.
  const measureConflicts = (() => {
    const out = [];
    for (const m of measures) {
      const nm = (m.name || '').trim();
      if (!nm) continue;
      const myPred = canonPred(serializePredicate(m.predicate));
      const mySrc = m.source_col || '';
      for (const b of existingBindings) {
        if (editAggregate && b.entity_id === editAggregate.entity_id) continue;
        const spec = (b.measure_map || {})[nm];
        if (!spec) continue;
        const theirPred = canonPred(spec.baked_predicate);
        const theirSrc = spec.source_col || '';
        if (theirPred !== myPred || theirSrc !== mySrc) {
          out.push({ measure: nm, binding: b.name || b.entity_id, src: theirSrc, pred: theirPred });
        }
      }
    }
    return out;
  })();

  const submit = async () => {
    if (!canonicalId) return toast.error('Pick a canonical fact');
    if (!grain.length) return toast.error('Pick at least one grain column');
    if (!measures.length) return toast.error('Pick at least one measure');
    const names = measures.map(m => (m.name || '').trim());
    if (names.some(n => !n)) return toast.error('Every stored column needs a name');
    if (new Set(names).size !== names.length) return toast.error('Stored column names must be unique');
    if (measures.some(m => !m.source_col)) return toast.error('Every measure needs a source column');
    if (mode === 'managed' && measures.some(m => m.additivity === 'semi_additive')) return toast.error('Semi-additive (level) measures are external-only for now — use External mode');

    // Display name is optional — fall back to a sensible default so the user is
    // never blocked by a "name is required" error (matches the managed path).
    const finalName = (name || '').trim() || `${canonical.name} (rollup)`;

    const measure_map = {};
    measures.forEach(m => {
      const spec = { source_col: m.source_col, agg_stored: m.agg_stored };
      // Physical column on the summary table when it differs from the measure
      // name — router serves FROM the summary using this column (else the
      // measure name). Only persist when the modeler set an explicit override.
      const sumCol = (m.summary_col || '').trim();
      if (sumCol && sumCol !== m.name.trim()) spec.summary_col = sumCol;
      const preds = serializePredicate(m.predicate);
      if (preds) spec.baked_predicate = preds;
      if (m.additivity === 'semi_additive') {
        spec.additivity = 'semi_additive';
        spec.time_dim = m.time_dim || grain.find(g => /month|period|date|quarter|year|week|day|yyyymm/i.test(g)) || grain[0] || '';
      }
      measure_map[m.name.trim()] = spec;
    });
    const refresh = mode === 'managed'
      ? { kind: 'managed', schedule: refreshKind, lag_watermark_column: 'last_updated_at', freshness_sla_minutes: refreshKind === 'streaming' ? 60 : 1440 }
      : { kind: 'external', schedule: 'manual', lag_watermark_column: watermarkCol || null, freshness_sla_minutes: 1440 };

    // Conformed dimension mappings — drop identity entries (summary column that
    // already matches the canonical by name resolves without a mapping).
    const dimension_map = {};
    Object.entries(dimensionMap || {}).forEach(([sdim, v]) => {
      if (v && typeof v === 'object') {
        if (v.col && v.col !== '__none__') dimension_map[sdim] = v;   // period mapping
      } else if (v && v !== '__none__' && v !== sdim) {
        dimension_map[sdim] = v;                                       // plain column mapping
      }
    });
    const time_grain = timeGrain && timeGrain !== '__none__' ? timeGrain : null;

    setLoading(true);
    setBuildStatus(null);
    try {
      if (editAggregate) {
        const _payload = { name: finalName, grain_columns: grain, measure_map, decomposition_dims: decompositionDims, dimension_map, time_grain };
        // External edit: send the (possibly self-healed) summary binding so a
        // stale/orphaned summary_entity_id gets repaired on save.
        if (mode === 'external' && summaryEntityId) { _payload.summary_entity_id = summaryEntityId; _payload.table_name = tableName; }
        await api.updateAggregate(editAggregate.entity_id, _payload);
        toast.success('Aggregate updated');
        if (onCreated) onCreated();
        setLoading(false); reset(); onOpenChange(false);
        return;
      }
      if (mode === 'managed') {
        const res = await api.buildManagedAggregate(modelId, { canonical_entity_id: canonicalId, name: finalName, grain_columns: grain, measure_map, refresh, decomposition_dims: decompositionDims, dimension_map, time_grain });
        // Also set the cron-driven schedule via preset so the background
        // refresher picks it up.
        try {
          if (res.aggregate?.entity_id && refreshKind && refreshKind !== 'manual') {
            await api.updateAggregateSchedule(res.aggregate.entity_id, { preset: refreshKind });
          }
        } catch { /* non-fatal — user can retry from the panel */ }
        setBuildStatus({ rows: res.built_rows, entity_id: res.aggregate?.entity_id, table: res.aggregate?.table_name });
        toast.success(`Aggregate built — ${res.built_rows} rows`);
      } else {
        if (!summaryEntityId) { setLoading(false); return toast.error('Pick an existing summary table (modeled entity) to bind'); }
        const res = await api.registerExternalAggregate(modelId, { canonical_entity_id: canonicalId, name: finalName, summary_entity_id: summaryEntityId, table_name: tableName, grain_columns: grain, measure_map, refresh, decomposition_dims: decompositionDims, dimension_map, time_grain });
        setBuildStatus({ rows: null, entity_id: res.entity_id, table: res.table_name, external: true });
        toast.success('External aggregate bound');
      }
      if (onCreated) onCreated();
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Failed to register aggregate');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMode('managed'); setCanonicalId(''); setName(''); setTableName('');
    setSummaryEntityId(''); setTableColumns([]); setManualWatermark(false);
    setGrain([]); setMeasures([]); setRefreshKind('nightly'); setWatermarkCol('last_updated_at');
    setManualDecomp([]);
    setDimensionMap({}); setTimeGrain('');
    setExtraMapKeys([]); setNewMapCol(''); setAddMapError('');
    setBuildStatus(null);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <SheetContent side="right" className="w-full sm:w-[560px] sm:max-w-[560px] overflow-y-auto" data-testid="aggregate-wizard">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Layers className="w-4 h-4 text-primary" />{editAggregate ? 'Edit Aggregate' : 'Add Aggregate'}</SheetTitle>
        </SheetHeader>
        {editHydrating ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-center" data-testid="aggregate-edit-loading">
            <Loader2 className="w-7 h-7 text-primary animate-spin" />
            <div>
              <p className="text-sm font-medium text-foreground">Loading aggregate…</p>
              <p className="text-xs text-muted-foreground mt-1">Fetching its canonical fact, summary columns and measure mappings. This can take a few seconds on large models.</p>
            </div>
          </div>
        ) : (
        <div className="mt-4 space-y-5">
          {/* Mode selector */}
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1.5 block">How is this aggregate maintained?</Label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setMode('managed')} className={`p-3 rounded-xl border text-left transition-all ${mode === 'managed' ? 'border-primary/60 bg-primary/5' : 'border-border/30 hover:border-border/60'}`} data-testid="mode-managed">
                <div className="flex items-center gap-1.5 mb-0.5"><RefreshCcw className="w-3 h-3 text-primary" /><span className="text-xs font-medium">Managed</span></div>
                <p className="text-[10px] text-muted-foreground leading-tight">{brandName} builds & refreshes the table from the canonical.</p>
              </button>
              <button onClick={() => setMode('external')} className={`p-3 rounded-xl border text-left transition-all ${mode === 'external' ? 'border-primary/60 bg-primary/5' : 'border-border/30 hover:border-border/60'}`} data-testid="mode-external">
                <div className="flex items-center gap-1.5 mb-0.5"><Table2 className="w-3 h-3 text-primary" /><span className="text-xs font-medium">External</span></div>
                <p className="text-[10px] text-muted-foreground leading-tight">Your ETL owns the table — {brandName} runs a drift check.</p>
              </button>
            </div>
          </div>

          {/* Canonical picker */}
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1.5 block">Canonical Fact</Label>
            <Select value={canonicalId} onValueChange={setCanonicalId}>
              <SelectTrigger className="h-8 text-xs" data-testid="canonical-select"><SelectValue placeholder="Pick a fact entity to roll up..." /></SelectTrigger>
              <SelectContent>{candidates.map(c => <SelectItem key={c.entity_id} value={c.entity_id}>{c.name} <span className="text-[10px] text-muted-foreground ml-1">({c.attributes.length} fields)</span></SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Orphan guard — editing an aggregate whose canonical was deleted */}
          {editAggregate && canonicalId && candidates.length > 0 && !canonical && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300" data-testid="canonical-missing-notice">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div className="text-[11px] leading-relaxed">
                <p className="font-medium">Canonical fact missing</p>
                <p className="text-amber-300/80">The fact entity this aggregate rolled up (<span className="font-mono">{editAggregate.derived_from_entity}</span>) no longer exists. Pick a canonical fact above to re-link it and continue editing.</p>
              </div>
            </div>
          )}

          {canonical && (<>
            {/* Name + summary binding */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1 block">Display Name <span className="text-muted-foreground/50 normal-case tracking-normal">(optional)</span></Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={`${canonical.name} (rollup)`} className="h-8 text-xs" data-testid="agg-name" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1 block">{mode === 'external' ? 'Summary table (existing entity)' : 'Target table (auto)'}</Label>
                {mode === 'external' ? (
                  <Select value={summaryEntityId} onValueChange={chooseSummaryEntity}>
                    <SelectTrigger className="h-8 text-xs" data-testid="summary-entity-select"><SelectValue placeholder="Bind to a modeled table…" /></SelectTrigger>
                    <SelectContent>
                      {summaryEntityOptions.length === 0 && <SelectItem value="__none__" disabled className="text-xs italic">No modeled tables available</SelectItem>}
                      {summaryEntityOptions.map(s => (
                        <SelectItem key={s.entity_id} value={s.entity_id} className="text-xs" data-testid={`summary-entity-option-${s.entity_id}`}>
                          {s.name}{s.already_bound && <span className="ml-1 text-[9px] text-amber-400/80">(bound)</span>}
                          <span className="ml-1 text-[9px] text-muted-foreground font-mono">{s.table_name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={tableName} onChange={(e) => setTableName(e.target.value)} placeholder="auto" className="h-8 text-xs font-mono" data-testid="agg-table" disabled />
                )}
              </div>
            </div>
            {mode === 'external' && (
              <p className="text-[10px] text-muted-foreground/70 -mt-2">Binds to an already-modeled summary entity — {brandName} does not create a duplicate table. Map its stored columns to canonical sources below.</p>
            )}

            {/* Grain */}
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1.5 block">Grain — one row per unique combination of these</Label>
              <div className="flex flex-wrap gap-1.5">
                {grainFields.map(d => (
                  <button key={d.name} onClick={() => toggleGrain(d.name)} className={`px-2 py-1 rounded-lg text-[10px] border transition-all ${grain.includes(d.name) ? 'border-primary/60 bg-primary/10 text-primary' : 'border-border/30 text-muted-foreground hover:border-border/60'}`} data-testid={`grain-${d.name}`}>{d.display_name || d.name}{d.semantic_type === 'hidden' && <span className="ml-1 text-[8px] text-muted-foreground/70">(hidden)</span>}</button>
                ))}
              </div>
              {grain.length > 0 && <p className="text-[10px] text-muted-foreground/70 mt-1.5">Grain: <span className="font-mono">{grain.join(' × ')}</span></p>}
            </div>

            {/* Conformed dimension mappings + summary time grain */}
            {grain.length > 0 && (
              <div data-testid="dimension-mappings">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-[10px] uppercase text-muted-foreground tracking-wider block">Dimension mappings — summary → canonical</Label>
                  {editAggregate?.entity_id && (
                    <Button type="button" variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground" onClick={refreshSummaryColumns} disabled={refreshingCols} data-testid="refresh-summary-cols-btn" title="Re-read the summary table's physical columns from its data source">
                      {refreshingCols ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCcw className="w-3 h-3 mr-1" />} Refresh columns from source
                    </Button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground/70 mb-1.5">Map a summary column to its conformed column on the canonical fact (e.g. <span className="font-mono">BOOK_END_DATE → TRANSACTION_BOOK_DATE</span>). For a period column (e.g. a year/quarter/month column like <span className="font-mono">BOOK_YEAR</span>), pick the canonical date column and set the period part (e.g. <span className="font-mono">year(TRANSACTION_BOOK_DATE)</span>). This lets AI Explore route a fine-grain / date-specific question to the transaction fact even when columns are named differently.</p>
                <div className="space-y-1">
                  {mapRowKeys.map(g => {
                    const conformedByName = canonicalColSet.has(g);
                    const raw = dimensionMap[g];
                    const isObj = raw && typeof raw === 'object';
                    const val = isObj ? raw.col : (raw || (conformedByName ? g : '__none__'));
                    const part = isObj ? (raw.part || '__none__') : '__none__';
                    const setCol = (v) => setDimensionMap(prev => {
                      const next = { ...prev };
                      if (v === '__none__' || (v === g && part === '__none__')) { delete next[g]; }
                      else { next[g] = part !== '__none__' ? { col: v, part } : v; }
                      return next;
                    });
                    const setPart = (p) => setDimensionMap(prev => {
                      const next = { ...prev };
                      const cur = next[g];
                      const col = (cur && typeof cur === 'object') ? cur.col : (typeof cur === 'string' ? cur : (conformedByName ? g : ''));
                      if (!col || col === '__none__') return next;
                      next[g] = (p && p !== '__none__') ? { col, part: p } : col;
                      return next;
                    });
                    return (
                      <div key={g} className="flex items-center gap-1.5" data-testid={`dimmap-row-${g}`}>
                        <span className="text-[10px] font-mono flex-1 min-w-0 truncate text-muted-foreground">{g}</span>
                        <span className="text-[9px] text-muted-foreground">→</span>
                        <SearchableSelect
                          value={val}
                          onChange={setCol}
                          options={[{ value: '__none__', label: '— none (same-name / not conformed) —' }, ...canonicalColNames.map(cc => ({ value: cc, label: cc }))]}
                          placeholder="canonical column…"
                          className="w-[150px]"
                          triggerClassName="h-6 text-[10px] font-mono"
                          align="right"
                          testId={`dimmap-select-${g}`}
                        />
                        <Select value={part} onValueChange={setPart}>
                          <SelectTrigger className="h-6 w-[86px] text-[10px]" data-testid={`dimmap-part-${g}`} title="For period columns (e.g. a year/quarter/month column) — derive it from the canonical date"><SelectValue /></SelectTrigger>
                          <SelectContent>{PERIOD_PARTS.map(p => <SelectItem key={p.value} value={p.value} className="text-[10px]">{p.label}</SelectItem>)}</SelectContent>
                        </Select>
                        {!grain.includes(g) && (
                          <button type="button" onClick={() => { setExtraMapKeys(prev => prev.filter(k => k !== g)); setDimensionMap(prev => { const n = { ...prev }; delete n[g]; return n; }); }} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`dimmap-remove-${g}`} title="Remove this mapping">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1.5 flex items-center gap-1.5" data-testid="dimmap-add-row">
                  <SearchableSelect
                    value={newMapCol}
                    onChange={(v) => { setNewMapCol(v); if (addMapError) setAddMapError(''); addMapKey(v); }}
                    options={summaryColOptions.filter(c => !mapRowKeySet.has(c)).map(c => ({ value: c, label: c }))}
                    allowCustom={summaryColOptions.length === 0}
                    placeholder="add summary column (e.g. BOOK_END_DATE)…"
                    searchPlaceholder="Search summary columns…"
                    className="flex-1"
                    triggerClassName="h-6 !text-[10px] font-mono"
                    align="right"
                    testId="dimmap-add-input"
                  />
                </div>
                {addMapError && <p className="text-[10px] text-destructive mt-1" data-testid="dimmap-add-error">{addMapError}</p>}
                <p className="text-[10px] text-muted-foreground/70 mt-1">The <b>left</b> column lists this summary table&apos;s own columns{tableName ? <> (<span className="font-mono">{tableName}</span>)</> : null}; the <b>right</b> dropdown is the canonical/transaction column it maps to. Grain columns are listed automatically. Add any other real summary column here — e.g. a date/period column like <span className="font-mono">BOOK_END_DATE</span> — then map it to the canonical date (optionally with a period part). {summaryColOptions.length ? `${summaryColOptions.length} summary columns available.` : ''}</p>
                <div className="mt-2 flex items-center gap-1.5">
                  <Label className="text-[10px] uppercase text-muted-foreground tracking-wider">Summary time grain</Label>
                  <Select value={timeGrain || '__none__'} onValueChange={(v) => setTimeGrain(v === '__none__' ? '' : v)}>
                    <SelectTrigger className="h-6 w-[170px] text-[10px]" data-testid="time-grain-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIME_GRAINS.map(t => <SelectItem key={t.value} value={t.value} className="text-[10px]">{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <p className="text-[10px] text-muted-foreground/70 mt-1">A question at a finer grain than this (e.g. weekly against a monthly summary) routes to the transaction fact for accuracy.</p>
                {timeGrainMissingDateCol && (
                  <div className="mt-2 flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300" data-testid="time-grain-no-date-warning">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div className="text-[11px] leading-relaxed">
                      <p className="font-medium">No date/period column at this grain</p>
                      <p className="text-amber-700/80 dark:text-amber-300/80">This summary declares a <span className="font-mono">{timeGrain}</span> time grain but none of its grain columns is a date/timestamp, and no dimension mapping derives a period. Any date-based question will always fall back to the transaction fact — this summary can never serve one. Add a date/period column to the grain above, or map one to a canonical date (optionally with a period part) under Dimension mappings.</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Measures */}
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1.5 block">Measures to store</Label>
              <p className="text-[10px] text-muted-foreground/70 mb-1.5">Each stored column = an aggregate of a source column in the canonical. For a <span className="font-mono">wide</span> summary (e.g. one column per amount type) add the same source more than once and rename the stored column.</p>
              <div className="grid grid-cols-3 gap-1 mb-2">
                {measureFields.map(m => (
                  <button key={m.name} onClick={() => addMeasure(m)} title={`${m.display_name || m.name}${m.semantic_type === 'hidden' ? ' (hidden)' : ''}`} className="px-2 py-1 rounded-lg text-[10px] border border-dashed border-border/40 text-muted-foreground hover:border-primary/60 hover:text-primary transition-all text-left truncate whitespace-nowrap overflow-hidden min-w-0" data-testid={`add-measure-${m.name}`}>+ {m.display_name || m.name}{m.semantic_type === 'hidden' && ' (hidden)'}</button>
                ))}
              </div>
              {measures.length > 0 && (
                <div className="space-y-1">
                  {measures.map(m => (
                    <div key={m.uid} className="p-1.5 rounded-lg bg-muted/20 border border-border/20" data-testid={`measure-row-${m.name || m.uid}`}>
                      <div className="flex items-center gap-1">
                        <Input value={m.name} onChange={(e) => updateMeasure(m.uid, { name: e.target.value })} placeholder="stored column" title="Column name in the summary table" className="h-6 flex-1 min-w-0 text-[10px] font-mono" data-testid={`measure-name-${m.uid}`} />
                        <span className="text-[9px] text-muted-foreground">=</span>
                        <Select value={m.agg_stored} onValueChange={(v) => updateMeasureKind(m.uid, v)}>
                          <SelectTrigger className="h-6 w-[92px] text-[10px]" data-testid={`kind-${m.uid}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{AGG_KINDS.map(k => <SelectItem key={k.value} value={k.value}><div><p>{k.label}</p><p className="text-[9px] text-muted-foreground">{k.hint}</p></div></SelectItem>)}</SelectContent>
                        </Select>
                        <span className="text-[9px] text-muted-foreground">of</span>
                        <Select value={m.source_col} onValueChange={(v) => updateMeasure(m.uid, { source_col: v })}>
                          <SelectTrigger className="h-6 w-[112px] text-[10px] font-mono" title="Source column in the canonical fact" data-testid={`source-${m.uid}`}><SelectValue placeholder="source col" /></SelectTrigger>
                          <SelectContent>{measureFields.map(f => <SelectItem key={f.name} value={f.name} className="text-[10px] font-mono">{f.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input value={m.summary_col || ''} onChange={(e) => updateMeasure(m.uid, { summary_col: e.target.value })} placeholder="summary col" title="Physical column on the summary table — leave blank if it matches the measure name" className="h-6 w-[104px] min-w-0 text-[10px] font-mono" data-testid={`summary-col-${m.uid}`} />
                        <button onClick={() => removeMeasure(m.uid)} className="text-[10px] text-muted-foreground hover:text-destructive px-1">×</button>
                      </div>
                      {/* Additivity: additive flow vs semi-additive level (LAST across time) */}
                      <div className="mt-1 pl-1 flex items-center gap-1.5 flex-wrap" data-testid={`additivity-row-${m.uid}`}>
                        <span className="text-[9px] text-muted-foreground/70">Rolls up as</span>
                        <Select value={m.additivity || 'additive'} onValueChange={(v) => updateMeasure(m.uid, { additivity: v })}>
                          <SelectTrigger className="h-6 w-[150px] text-[10px]" data-testid={`additivity-${m.uid}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="additive"><div><p>Additive (flow)</p><p className="text-[9px] text-muted-foreground">sums across time & dims — premium, fees</p></div></SelectItem>
                            <SelectItem value="semi_additive"><div><p>Semi-additive (level)</p><p className="text-[9px] text-muted-foreground">LAST across time — in-force count, reserve</p></div></SelectItem>
                          </SelectContent>
                        </Select>
                        {m.additivity === 'semi_additive' && (
                          <>
                            <span className="text-[9px] text-muted-foreground/70">over time</span>
                            <Select value={m.time_dim || ''} onValueChange={(v) => updateMeasure(m.uid, { time_dim: v })}>
                              <SelectTrigger className="h-6 w-[110px] text-[10px]" title="Period column used for LAST-in-period" data-testid={`time-dim-${m.uid}`}><SelectValue placeholder="time dim…" /></SelectTrigger>
                              <SelectContent>{grain.length ? grain.map(g => <SelectItem key={g} value={g} className="text-[10px] font-mono">{g}</SelectItem>) : <SelectItem value="__none__" disabled className="text-[10px]">pick grain first</SelectItem>}</SelectContent>
                            </Select>
                          </>
                        )}
                      </div>
                      {/* Conditional-aggregate predicate: the filter baked into
                          this column — supports nested AND/OR/NOT groups. */}
                      <div className="mt-1 pl-1" data-testid={`predicate-editor-${m.uid}`}>
                        {measures.filter(x => x.uid !== m.uid && serializePredicate(x.predicate)).length > 0 && (
                          <div className="flex items-center gap-1.5 mb-1" data-testid={`copy-predicate-row-${m.uid}`}>
                            <span className="text-[9px] text-muted-foreground/70">Copy filter from</span>
                            <Select value="" onValueChange={(uid) => { const src = measures.find(x => x.uid === uid); if (src) updateMeasure(m.uid, { predicate: JSON.parse(JSON.stringify(src.predicate)) }); }}>
                              <SelectTrigger className="h-6 w-[160px] text-[10px]" data-testid={`copy-predicate-${m.uid}`}><SelectValue placeholder="another measure…" /></SelectTrigger>
                              <SelectContent>
                                {measures.filter(x => x.uid !== m.uid && serializePredicate(x.predicate)).map(x => (
                                  <SelectItem key={x.uid} value={x.uid} className="text-[10px]" data-testid={`copy-predicate-${m.uid}-from-${x.uid}`}>{x.name || x.source_col || 'measure'}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {editFields(m.predicate).length > 0 && (
                          <p className="text-[9px] text-muted-foreground/70 mb-1"><span className="font-mono">{m.name || 'column'}</span> = {m.agg_stored}(<span className="font-mono">{m.source_col}</span>) WHERE:</p>
                        )}
                        {predToText(m.predicate) && (
                          <div className="mb-1 rounded-md border border-primary/20 bg-primary/5 px-2 py-1" data-testid={`predicate-preview-${m.uid}`}>
                            <span className="text-[9px] text-primary/70 font-medium mr-1">WHERE</span>
                            <span className="text-[10px] font-mono text-foreground/90 break-words">{predToText(m.predicate)}</span>
                          </div>
                        )}
                        <PredicateGroup
                          node={m.predicate || { kind: 'group', bool: 'and', negated: false, children: [] }}
                          onChange={(tree) => updateMeasure(m.uid, { predicate: tree })}
                          fields={grainFields}
                          depth={0}
                          tid={`predicate-${m.uid}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Decomposition dimensions — consumed by predicates, unsliceable from the summary */}
            {measures.length > 0 && (
              <div data-testid="decomposition-dims">
                <Label className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1.5 block">Decomposition dimensions</Label>
                <p className="text-[10px] text-muted-foreground/70 mb-1.5">Baked into columns above — queries that slice by these silently fall back to the transaction grain.</p>
                <div className="flex flex-wrap gap-1.5">
                  {decompositionDims.length === 0 && <span className="text-[10px] text-muted-foreground/80">none — no predicates defined</span>}
                  {autoDecomp.map(d => (
                    <span key={d} className="px-2 py-0.5 rounded-lg text-[10px] border border-primary/40 bg-primary/10 text-primary" title="auto from predicate">{d}</span>
                  ))}
                  {grainFields.filter(d => !autoDecomp.includes(d.name)).map(d => (
                    <button key={d.name} onClick={() => setManualDecomp(prev => prev.includes(d.name) ? prev.filter(x => x !== d.name) : [...prev, d.name])} className={`px-2 py-0.5 rounded-lg text-[10px] border transition-all ${manualDecomp.includes(d.name) ? 'border-amber-500/50 bg-amber-500/10 text-amber-300' : 'border-border/30 text-muted-foreground hover:border-border/60'}`} data-testid={`decomp-${d.name}`}>{d.name}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Refresh policy */}
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1.5 block">{mode === 'managed' ? 'Refresh cadence' : 'Watermark column'}</Label>
              {mode === 'managed' ? (
                <Select value={refreshKind} onValueChange={setRefreshKind}>
                  <SelectTrigger className="h-8 text-xs" data-testid="refresh-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="every-5m">Every 5 minutes (near-realtime)</SelectItem>
                    <SelectItem value="every-15m">Every 15 minutes</SelectItem>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="nightly">Nightly (default)</SelectItem>
                    <SelectItem value="manual">Manual only</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="space-y-1">
                  {tableColumns.length > 0 && !manualWatermark ? (
                    <Select value={watermarkCol || '__none__'} onValueChange={(v) => { if (v === '__manual__') { setManualWatermark(true); setWatermarkCol(''); } else { setWatermarkCol(v === '__none__' ? '' : v); } }}>
                      <SelectTrigger className="h-8 text-xs font-mono" data-testid="watermark-select"><SelectValue placeholder="Choose a column…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__" className="text-xs italic">— none (truncate + reload) —</SelectItem>
                        {tableColumns.map(c => <SelectItem key={c} value={c} className="text-xs font-mono" data-testid={`watermark-option-${c}`}>{c}</SelectItem>)}
                        <SelectItem value="__manual__" className="text-xs italic">✎ Enter column manually…</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={watermarkCol} onChange={(e) => setWatermarkCol(e.target.value)} placeholder={tableName ? 'last_updated_at (leave blank if truncate+reload)' : 'pick a table first, or type a column'} className="h-8 text-xs font-mono" data-testid="watermark-col" />
                  )}
                  <p className="text-[10px] text-muted-foreground/70">Without a watermark column, hybrid union is disabled — router falls back to canonical when this aggregate is stale.</p>
                </div>
              )}
            </div>

            {/* Mapping conflict guard — same stored column defined differently elsewhere */}
            {measureConflicts.length > 0 && (
              <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/30 text-[11px] text-amber-300 space-y-1" data-testid="measure-conflict-warning">
                <div className="flex items-center gap-1.5 font-medium"><AlertTriangle className="w-3 h-3" />Measure name conflict across bindings</div>
                {measureConflicts.map((c, i) => (
                  <p key={i} className="text-amber-300/90 leading-tight" data-testid={`conflict-${c.measure}-${i}`}>
                    <span className="font-mono">{c.measure}</span> is already defined by <span className="font-medium">{c.binding}</span> as
                    {' '}<span className="font-mono">SUM({c.src || '?'})</span>{c.pred ? <> WHERE <span className="font-mono">{c.pred}</span></> : ' (no predicate)'} — a different definition. This makes the canonical scoped measure ambiguous; rename this column or align the two bindings.
                  </p>
                ))}
              </div>
            )}

            {/* Build result */}
            {buildStatus && (
              <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-[11px] text-emerald-300" data-testid="build-status">
                <div className="flex items-center gap-1.5 mb-1"><CheckCircle2 className="w-3 h-3" /><span className="font-medium">{buildStatus.external ? 'Registered' : 'Built'}</span></div>
                <p><span className="text-emerald-300/70">Entity:</span> <span className="font-mono">{buildStatus.entity_id}</span></p>
                <p><span className="text-emerald-300/70">Table:</span> <span className="font-mono">{buildStatus.table}</span></p>
                {buildStatus.rows !== null && <p><span className="text-emerald-300/70">Rows:</span> <span className="font-mono">{buildStatus.rows.toLocaleString()}</span></p>}
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-border/30">
              <Button onClick={submit} disabled={loading || !grain.length || !measures.length} className="flex-1 h-8" data-testid="submit-aggregate">{loading ? <Loader2 className="w-3 h-3 animate-spin" /> : editAggregate ? 'Save Changes' : mode === 'managed' ? 'Build Aggregate' : 'Bind External'}</Button>
              <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} className="h-8">Cancel</Button>
            </div>
          </>)}
          {!canonical && candidates.length === 0 && (
            <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-400">
              <AlertTriangle className="w-3 h-3 inline mr-1" />No canonical fact entities found in this model.
            </div>
          )}
        </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
